import { useSiteContext } from 'src/store';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { logEventAnalytic } from 'src/utilities/logAnalytics';
import { PageLoading } from 'src/components/PageLoading';
import {
  AppDetailsProvider,
  IDynamicContent,
} from '@orcha/micro-library-app-details-ui';
import axios from 'axios';
import { AppDetailsPageProps } from './AppDetailsPage.types';
import Error from '../../../pages/_error';
import { PageWithContext } from './PageWithContext';

export const AppDetailsPage = ({
  pageData,
  breadcrumbUrl,
}: AppDetailsPageProps) => {
  const { app } = pageData ?? {};
  const { dynamicContentAccountType } = useSiteContext();
  const dynamicContentApiUrl = `/api/dynamiccontent/${dynamicContentAccountType}`;
  const [dynamicContent, setDynamicContent] = useState<
    IDynamicContent | undefined
  >(undefined);

  const { basePath, asPath, locale } = useRouter();

  const [hostUrl, setHostUrl] = useState<string>('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { host } = window.location;
      setHostUrl(host);
    }
  }, []);

  if (!hostUrl) {
    return <PageLoading />;
  }

  const handleDownloadButtonClick = () => {
    logEventAnalytic({
      category: 'Download',
      action: 'Opened download dialog from app details - microsite',
      label: `${app?.appId} ${app?.name}`,
    });
  };

  const handlePlatformTabChange = (platformName: string) => {
    logEventAnalytic({
      category: 'AppDetails',
      action: 'Changed platform tab - microsite',
      label: `${app?.appId} ${app?.name} ${platformName}`,
    });
  };

  const handleScoreTabChange = (tabName: string) => {
    logEventAnalytic({
      category: 'App Details',
      action: 'Click on app score details tab - microsite',
      label: tabName,
    });
  };
  const getFirstReviewId = () => {
    const platforms = app?.platforms;
    if (!Array.isArray(platforms) || platforms.length === 0) {
      return undefined;
    }

    const accs = platforms[0]?.accreditations;

    if (!Array.isArray(accs) || accs.length === 0) {
      return undefined;
    }

    return accs[0]?.reviewId;
  };

  const handleRequestDynamicContent = async (reviewId: number) => {
    try {
      const { data } = await axios.get(
        `${dynamicContentApiUrl}?reviewId=${reviewId}`
      );
      setDynamicContent(data.dynamicContent);
    } catch (error) {
      setDynamicContent(undefined);
    }
  };

  const firstReviewId = getFirstReviewId();
  if (!dynamicContent && firstReviewId) {
    handleRequestDynamicContent(firstReviewId);
  }

  if (pageData?.app && pageData?.widgets) {
    return (
      <AppDetailsProvider
        app={pageData.app}
        widgets={pageData.widgets}
        eventHandlers={{
          handleDownloadButtonClick,
          handlePlatformTabChange,
          handleScoreTabChange,
          handleRequestDynamicContent,
        }}
        dynamicContentApiUrl={dynamicContentApiUrl}
        dynamicContent={dynamicContent}
      >
        <PageWithContext
          breadcrumbUrl={breadcrumbUrl}
          basePath={basePath}
          asPath={asPath}
          hostUrl={hostUrl}
          locale={locale}
        />
      </AppDetailsProvider>
    );
  }

  return <Error statusCode={!pageData?.app ? 404 : 500} />;
};
